import React, { createContext, useState, useContext, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import Cookies from 'js-cookie';

declare module '@mui/material/styles' {
  interface Palette {
    status: {
      complete: string;
      pending: string;
      retrying: string;
      error: string;
      notStarted: string;
    };
  }

  interface PaletteOptions {
    status?: {
      complete?: string;
      pending?: string;
      retrying?: string;
      error?: string;
      notStarted?: string;
    };
  }
}

interface IThemeContext {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<IThemeContext | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    return Cookies.get('darkMode') === 'true';
  });

  const toggleDarkMode = () => {
    const newValue = !darkMode;
    setDarkMode(newValue);
    Cookies.set('darkMode', newValue.toString(), { expires: 365 });
  };

  // Memoize the theme to prevent unnecessary recalculations on every render
  const theme = useMemo(() => createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#000' : '#fff',
      },
      action: {
        selected: darkMode ? '#3c3c3c' : '#fff',
      },
      background: {
        default: darkMode ? '#333' : '#ddd',
        paper: darkMode ? '#000' : '#fff',
      },
      text: {
        primary: darkMode ? '#fff' : '#111',
      },
      status: {
        complete: '#4caf50',   // Vibrant Green (like default MUI success)   // Vibrant Green
        pending: '#fdd835',
        retrying: '#f57c00',   // Bright Orange (a bit deeper than pending)
        error: '#f44336',      // Vibrant Red (like default MUI error)
        notStarted: '#9e9e9e', // Lighter, neutral Grey
      },
    },
    components: {
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: darkMode ? '#fff' : '#000',
            '&.Mui-focused': {
              color: darkMode ? '#fff' : '#000',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: darkMode ? '#fff' : '#000',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? '#fff' : '#000',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? '#fff' : '#000',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: darkMode ? '#fff' : '#000',
            },
          },
        },
      },
    },
  }), [darkMode]);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};